import { useCallback, useRef } from "react";
import toast, { ToastPosition } from "react-hot-toast";
import UndoToast from "@/components/ui/UndoToast";

export const useUndoToast = <UndoData,>(position?: ToastPosition) => {
  const undoTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentToastId = useRef<string | null>(null);

  const showUndoToast = useCallback(
    (
      message: string,
      undoData: UndoData,
      onUndo: (undoData: UndoData) => Promise<void>
    ) => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
      if (currentToastId.current) {
        toast.dismiss(currentToastId.current);
      }

      const handleUndo = async () => {
        try {
          await onUndo(undoData);
          if (currentToastId.current) {
            toast.dismiss(currentToastId.current);
          }
        } catch (error) {
          toast.error("Failed to undo changes");
        }
      };

      const handleDismiss = () => {
        if (currentToastId.current) {
          toast.dismiss(currentToastId.current);
        }
        if (undoTimeoutRef.current) {
          clearTimeout(undoTimeoutRef.current);
        }
      };

      const toastId = toast.success(
        <UndoToast
          message={message}
          onUndo={handleUndo}
          onDismiss={handleDismiss}
        />,
        {
          duration: 5000,
          position
        }
      );

      currentToastId.current = toastId;

      undoTimeoutRef.current = setTimeout(() => {
        if (currentToastId.current) {
          toast.dismiss(currentToastId.current);
          currentToastId.current = null;
        }
      }, 5000);
    },
    [position]
  );

  return { showUndoToast };
};
